import React, { memo, useMemo } from "react";
import { useFiltro } from "../../../../../../contexts/FiltroContext.tsx";
import { opcoesBrfMapper } from "../../../../../../core/utils/elegivelBrf.ts";
import {
	FiltroEmbarqueCompletoEnum,
	FiltroEmbarqueCompletoTitles,
} from "../../../../../../models/FiltroEmbarqueCompleto.ts";
import { RenderLateralCheckbox, RenderCheckbox } from "./RenderCheckboxes.tsx";
import RenderCombinedFiltros from "./RenderCombinedFiltros.tsx";
import RenderStringFiltros from "./RenderStringFiltros.tsx";
import RenderHeaderFiltros from "./RenderHeaderFiltros.tsx";
import { opcoesTipoProprietarioMapper } from "../../../../../../core/utils/tipoProprietario.ts";

function ModalFiltroViagensContent({
	scrollRef,
	showDiariaExcedida = true,
}: {
	scrollRef: React.RefObject<HTMLDivElement>;
	showDiariaExcedida?: boolean;
}) {
	const { filtroEmbarque, setFiltro } = useFiltro();

	const canceladoOptions = useMemo(
		() => [
			{
				label: "Ambos",
				checked: filtroEmbarque.cancelado === null ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Cancelado, null);
				},
			},
			{
				label: "Sim",
				checked: filtroEmbarque.cancelado ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Cancelado, true);
				},
			},
			{
				label: "Não",
				checked: filtroEmbarque.cancelado === false ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Cancelado, false);
				},
			},
		],
		[filtroEmbarque.cancelado, setFiltro]
	);

	const frotaOptions = useMemo(
		() => [
			{
				label: "Ambos",
				checked: filtroEmbarque.frota === null ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Frota, null);
				},
			},
			{
				label: "Sim",
				checked: filtroEmbarque.frota ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Frota, true);
				},
			},
			{
				label: "Não",
				checked: filtroEmbarque.frota === false ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Frota, false);
				},
			},
		],
		[filtroEmbarque.frota, setFiltro]
	);

	const elegivelBrfOptions = useMemo(
		() =>
			opcoesBrfMapper.map((option) => ({
				label: option.label,
				checked:
					filtroEmbarque.elegibilidadeBrf?.includes(option.value) || false,
				onCheck: () => {
					if (filtroEmbarque.elegibilidadeBrf?.includes(option.value)) {
						setFiltro(
							FiltroEmbarqueCompletoEnum.ElegibilidadeBrf,
							filtroEmbarque.elegibilidadeBrf.filter(
								(item) => item !== option.value
							)
						);
						return;
					}
					const newElegibilidadeBrf = filtroEmbarque.elegibilidadeBrf
						? [...filtroEmbarque.elegibilidadeBrf, option.value]
						: [option.value];

					setFiltro(
						FiltroEmbarqueCompletoEnum.ElegibilidadeBrf,
						newElegibilidadeBrf
					);
				},
			})),
		[filtroEmbarque.elegibilidadeBrf, setFiltro]
	);

	const tipoProprietarioOptions = useMemo(
		() =>
			opcoesTipoProprietarioMapper.map((option) => ({
				label: option.label,
				checked:
					filtroEmbarque.tipoProprietario?.includes(option.value) || false,
				onCheck: () => {
					if (filtroEmbarque.tipoProprietario?.includes(option.value)) {
						setFiltro(
							FiltroEmbarqueCompletoEnum.TipoProprietario,
							filtroEmbarque.tipoProprietario.filter(
								(item) => item !== option.value
							)
						);
						return;
					}
					const newTipoProprietario = filtroEmbarque.tipoProprietario
						? [...filtroEmbarque.tipoProprietario, option.value]
						: [option.value];

					setFiltro(
						FiltroEmbarqueCompletoEnum.TipoProprietario,
						newTipoProprietario
					);
				},
			})),
		[filtroEmbarque.tipoProprietario, setFiltro]
	);

	const diariaExcedidaOptions = useMemo(
		() => [
			{
				label: "Ambos",
				checked: filtroEmbarque.diariaExcedida === null ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.DiariaExcedida, null);
				},
			},
			{
				label: "Sim",
				checked: filtroEmbarque.diariaExcedida ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.DiariaExcedida, true);
				},
			},
			{
				label: "Não",
				checked: filtroEmbarque.diariaExcedida === false ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.DiariaExcedida, false);
				},
			},
		],
		[filtroEmbarque.diariaExcedida, setFiltro]
	);

	const agregadoOptions = useMemo(
		() => [
			{
				label: "Ambos",
				checked: filtroEmbarque.agregado === null ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Agregado, null);
				},
			},
			{
				label: "Sim",
				checked: filtroEmbarque.agregado ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Agregado, true);
				},
			},
			{
				label: "Não",
				checked: filtroEmbarque.agregado === false ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Agregado, false);
				},
			},
		],
		[filtroEmbarque.agregado, setFiltro]
	);

	const telemetriaOptions = useMemo(
		() => [
			{
				label: "Ambos",
				checked: filtroEmbarque.telemetria === null ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Telemetria, null);
				},
			},
			{
				label: "Sim",
				checked: filtroEmbarque.telemetria ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Telemetria, true);
				},
			},
			{
				label: "Não",
				checked: filtroEmbarque.telemetria === false ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.Telemetria, false);
				},
			},
		],
		[filtroEmbarque.telemetria, setFiltro]
	);

	const pisoExcedidoOptions = useMemo(
		() => [
			{
				label: "Ambos",
				checked: filtroEmbarque.pisoExcedido === null ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.PisoExcedido, null);
				},
			},
			{
				label: "Sim",
				checked: filtroEmbarque.pisoExcedido ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.PisoExcedido, true);
				},
			},
			{
				label: "Não",
				checked: filtroEmbarque.pisoExcedido === false ? true : false,
				onCheck: () => {
					setFiltro(FiltroEmbarqueCompletoEnum.PisoExcedido, false);
				},
			},
		],
		[filtroEmbarque.pisoExcedido, setFiltro]
	);

	return (
		<div className="modal-filtros-viagens-body" ref={scrollRef}>
			<div className="modal-filtros-viagens-checkboxes-container">
				<RenderLateralCheckbox
					title="Elegível BRF"
					options={elegivelBrfOptions}
				/>
				<RenderLateralCheckbox
					title="Tipo Proprietário"
					options={tipoProprietarioOptions}
				/>
				<RenderCheckbox
					title={FiltroEmbarqueCompletoTitles.Cancelado}
					options={canceladoOptions}
				/>
				<RenderCheckbox
					title={FiltroEmbarqueCompletoTitles.Frota}
					options={frotaOptions}
				/>
				{showDiariaExcedida && (
					<RenderCheckbox
						title="Diaria Excedida"
						options={diariaExcedidaOptions}
					/>
				)}
				<RenderCheckbox
					title={FiltroEmbarqueCompletoTitles.Agregado}
					options={agregadoOptions}
				/>
				<RenderCheckbox
					title={FiltroEmbarqueCompletoTitles.Telemetria}
					options={telemetriaOptions}
				/>
				<RenderCheckbox
					title={FiltroEmbarqueCompletoTitles.PisoExcedido}
					options={pisoExcedidoOptions}
				/>
			</div>
			<div className="modal-filtros-viagens-combined-filtros-container">
				<RenderHeaderFiltros />
				<RenderCombinedFiltros />
				<RenderStringFiltros />
			</div>
		</div>
	);
}

export default memo(ModalFiltroViagensContent);
